'use client';

import { Textarea } from '@headlessui/react';
import { Button, Entering, Hint, Label, Select, SelectTags, Tag, Typography } from 'components';
import { ENTER } from 'constants/keyboard';
import {
  AiEngineEnum,
  ErrorCodeEnum,
  Maybe,
  PaymentMethodEnum,
  UniversalChatEngineEnum,
  UniversalChatRoleEnum
} from 'gql/graphql';
import { AiIcon, InfoHollowIcon, StopIcon, TaskConfigIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { NodeHtmlMarkdown } from 'node-html-markdown';
import { useUniversalChatMessageCreate } from 'providers/ChatProviders/useUniversalChatMessageCreate';
import { useUniversalChatThreadsList } from 'providers/ChatProviders/useUniversalChatThreadsList';
import { useContextLibraryDataAll } from 'providers/ContextLibraryProviders/useContextLibraryDataAll';
import { useContextLibraryTags } from 'providers/ContextLibraryProviders/useContextLibraryTags';
import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { Option } from 'types/Option';
import { cn } from 'utils/cn';
import { extractUrls } from 'utils/extractUrls';
import ModalUploadContextLibrary from '../../context-library/_components/UploaderContext/ModalUploadContextLibrary';
import useSearchParamsContextLibrary from '../../context-library/_context/useSearchParamsContextLibrary';
import ChatMsg from './_components/ChatMsg';
import ChatMsgProcessing from './_components/ChatMsgProcessing';
import HistoryItem from './_components/HistoryItem';
import ModalManageTaskPrompt from './_components/ModalManageTaskPrompt';

import { toaster } from 'helpers/toaster';
import useOptionsModels from 'hooks/useOptionsModels';
import { Socket } from 'phoenix';
import { useUniversalChatThreads } from 'providers/ChatProviders/useUniversalChatThreads';
import { useUniversalChatThreadsContexts } from 'providers/ChatProviders/useUniversalChatThreadsContexts';
import { useClientAccount } from 'providers/ClientProviders/useClientAccount';
import { useSocket } from 'providers/SocketProviders/useSocket';
import tc from 'thousands-counter';
import Attach from './_components/Attach';
import Attached from './_components/Attached';
import './_components/styles.scss';

interface IChatAiProps {
  params: { slug: Array<string> };
}

type TUniversalChatMessage = {
  role: UniversalChatRoleEnum;
  content: string;
  file?: File | null;
};

type TSocketPayload = {
  message: string;
  thread_id: number;
  thread_title: string;
  tokens: number;
};

let initial = true;

export default function ChatAi({ params }: IChatAiProps) {
  const t = useTranslations();

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [model, setModel] = useState<UniversalChatEngineEnum>(UniversalChatEngineEnum.ChatGpt4o);
  const [animateMessages, setAnimateMessages] = useState(false);
  const [tokens, setTokens] = useState(0);
  const [chatInitialized, setChatInitialized] = useState(false);
  const [text, setText] = useState('');
  const [height, setHeight] = useState(48);
  const [chatId, setChatId] = useState(params?.slug?.at(0));
  const [openManageTaskPrompt, setOpenManageTaskPromp] = useState(false);
  const [contextIds, setContextIds] = useState<string[]>([]);
  const [messagesHistory, setMessagesHistory] = useState<TUniversalChatMessage[]>([]);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [openModalAddFile, setOpenModalAddFile] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [file, setFile] = useState<File | null>();
  const [attachUrls, setAttachUrls] = useState<string[]>([]);
  const [textUrls, setTextUrls] = useState<string[]>([]);
  const [socketInitialized, setSocketInitialized] = useState(false);

  const sendDisabled = !text || !text.trim() || (!text && !file?.name);

  const { optionsModels } = useOptionsModels({
    blackList: [AiEngineEnum.Opus, AiEngineEnum.Opus_46, AiEngineEnum.ChatGpt5, AiEngineEnum.Haiku]
  });

  const { defaultTags, refetch } = useSearchParamsContextLibrary();

  const { data: socketConfig } = useSocket();

  const { data: clientAccount } = useClientAccount();

  const { data: dataTags, refetch: refetchTags } = useContextLibraryTags();

  const { data: chatList, refetch: refetchChatList } = useUniversalChatThreadsList();

  const { data: messages } = useUniversalChatThreads({ id: chatId }, { enabled: !!chatId });
  const { data: chatContexts, refetch: refetchChatContexts } = useUniversalChatThreadsContexts(
    { id: chatId },
    { enabled: !!chatId }
  );

  const { mutateAsync: requestPrompt, reset: onStop } = useUniversalChatMessageCreate({
    onError: (error: any) => {
      notifyException(error, ESpansException.AiException);
      if (error?.errorCode === ErrorCodeEnum.PaymentRequired)
        toaster.error(
          clientAccount?.paymentMethod === PaymentMethodEnum.CreditCard ||
            clientAccount?.paymentMethod === PaymentMethodEnum.BankTransfer
            ? t.raw('messages.error.ai_trial.paragraph.confirmed')
            : t.raw('messages.error.ai_trial.paragraph.unconfirmed'),
          t('messages.error.ai_trial.title')
        );
      else
        toaster.error(
          t('messages.error.chat_error.paragraph'),
          t('messages.error.chat_error.title')
        );
    }
  });

  const handleResetAllStates = () => {
    setModel(optionsModels[0]?.value as UniversalChatEngineEnum);
    setAnimateMessages(false);
    setTokens(0);
    setChatInitialized(false);
    setText('');
    setHeight(48);
    setChatId(undefined);
    setContextIds([]);
    setMessagesHistory([]);
    setSelectedTagId('');
    setIsPending(false);
    setFile(null);
    setAttachUrls([]);
    setTextUrls([]);
    setSocketInitialized(false);
  };

  const { data: contexts, refetch: refetchContexts } = useContextLibraryDataAll();

  const handleChangeAttachUrl = useCallback(
    (value: string) => setAttachUrls((oldState) => [...oldState, value]),
    [setAttachUrls]
  );

  const urls = useMemo(() => [...attachUrls, ...textUrls], [attachUrls, textUrls]);

  const contextsIncluded = useMemo(
    () => contexts?.nodes?.filter(({ id }) => contextIds.includes(id)),
    [contextIds, contexts?.nodes]
  );

  const optionsContexts = useMemo(
    () =>
      contexts?.nodes?.reduce(
        (acc, { filename, id, processed, errorMessage, tags }) =>
          (!selectedTagId || (selectedTagId && tags?.some(({ id }) => id === selectedTagId))) &&
          processed &&
          !errorMessage
            ? [...acc, { label: filename, value: id } as Option]
            : acc,
        [] as Option[]
      ),
    [contexts, selectedTagId]
  );

  const optionsFilterTags = useMemo(
    () => [
      {
        label: t('placeholders.search_tags'),
        value: ''
      },
      ...(dataTags?.map((tags) => ({
        label: tags.name,
        value: tags.id
      })) ?? [])
    ],
    [t, dataTags]
  );

  const chatContainerRef = useRef<HTMLDivElement>(null);

  const contextsDisabled =
    !contexts?.nodes?.length ||
    messagesHistory?.length > 0 ||
    model === UniversalChatEngineEnum.Anthropic ||
    model === UniversalChatEngineEnum.Tolq_3b;

  const handleToggleModal = () => setOpenModalAddFile((oldState) => !oldState);

  const handleRefetch = () => {
    refetch();
    refetchTags();
    refetchContexts();
  };

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []);

  const stopAnimation = useCallback(() => {
    setAnimateMessages(false);
    scrollToBottom();
  }, []);

  const updateContextIds = (values: string[]) => values && setContextIds(values);

  const deleteContextIds = (id: string) => () =>
    setContextIds((oldState) => oldState?.filter((contextId) => contextId !== id));

  const onNewChat = useCallback(() => {
    window.history.pushState(
      {
        as: '/smart-tasks/chat-ai',
        url: '/smart-tasks/chat-ai/[...slug]',
        options: { shallow: true }
      },
      '',
      '/smart-tasks/chat-ai'
    );

    handleResetAllStates();
  }, []);

  const onSelectModel = ({ value }: Option) => setModel(value as UniversalChatEngineEnum);

  const resizeTextarea = useCallback(
    (value: string) => {
      if (inputRef?.current?.style?.height) {
        inputRef.current.style.height = 'auto'; // Reset height
        inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
        setHeight(!value ? 48 : inputRef.current.scrollHeight);
      }
    },
    [inputRef]
  );

  const openLoadFile = useCallback(
    (value: string) => {
      if (inputRef?.current?.style?.height) {
        inputRef.current.style.height = 'auto'; // Reset height
        inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
        setHeight(!value ? 38 : inputRef.current.scrollHeight);
      }
    },
    [inputRef]
  );

  const onSubmit = useCallback(async () => {
    const message = {
      content: text,
      role: UniversalChatRoleEnum.User,
      file,
      urls,
      textUrls,
      attachUrls
    };
    if (!sendDisabled)
      try {
        scrollToBottom();
        setChatInitialized(true);
        setAnimateMessages(true);
        setMessagesHistory((oldState) => [...oldState, message]);
        setText('');
        setFile(null);
        setAttachUrls([]);
        setTextUrls([]);
        setIsPending(true);

        const response = await requestPrompt({
          message: {
            content: message.content,
            role: message.role
          },
          model,
          file,
          urls: message.urls,
          contextIds,
          threadId: chatId
        });

        if (response.thread) {
          if (response.thread.id !== chatId) {
            setChatId(response.thread.id);
            window.history.pushState(
              {
                as: `/smart-tasks/chat-ai/${response.thread.id}`,
                url: '/smart-tasks/chat-ai/[...slug]',
                options: { shallow: true }
              },
              '',
              `/smart-tasks/chat-ai/${response.thread.id}`
            );
          }
        }
      } catch (error) {
        setMessagesHistory((oldState) => oldState.slice(0, -1));
        setText(message.content);
        setFile(message.file);
        setAttachUrls(message?.attachUrls);
        setTextUrls(message?.textUrls);
      }
  }, [
    text,
    sendDisabled,
    model,
    chatId,
    contextIds,
    textUrls,
    attachUrls,
    urls,
    file,
    requestPrompt,
    setIsPending,
    setAttachUrls,
    scrollToBottom
  ]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === ENTER && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleOpenManageTaskPrompt = () => setOpenManageTaskPromp(true);
  const handleCloseManageTaskPrompt = () => setOpenManageTaskPromp(false);

  const handleLoadFile = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (files && files.length > 0) {
      setFile(files[0]);
      resizeTextarea('');
    }
  };

  const handleRemoveLink = (url: string) =>
    setAttachUrls((oldState) => oldState.filter((link) => link !== url));
  const handleRemoteFile = () => setFile(null);

  const onChangeTextarea = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setText(e.target.value);
      setTextUrls(extractUrls(e.target.value));
    },
    [inputRef]
  );

  const onPasteTextarea = useCallback(
    async (e: ClipboardEvent<HTMLTextAreaElement>) => {
      e.preventDefault();

      const pastedText = e.clipboardData.getData('text/html') || e.clipboardData.getData('text');

      if (pastedText) {
        // Convert rich text (HTML or otherwise) to markdown
        const markdown = NodeHtmlMarkdown.translate(
          /* html */ pastedText,
          /* options (optional) */ {}
        );

        // Insert the markdown at the cursor position without preventing the default paste
        const selectionStart = e.currentTarget?.selectionStart;
        const selectionEnd = e.currentTarget?.selectionEnd;
        if (selectionStart && selectionEnd) {
          const beforePaste = text.slice(0, selectionStart);
          const afterPaste = text.slice(selectionEnd);

          setText(beforePaste + markdown + afterPaste);
          setTextUrls(extractUrls(beforePaste + markdown + afterPaste));

          // Set cursor position after the pasted markdown
          e.currentTarget.selectionStart = selectionStart + markdown.length;
          e.currentTarget.selectionEnd = selectionStart + markdown.length;

          e.preventDefault(); // Prevent default paste to avoid duplicating content
        } else {
          setText(markdown);
          setTextUrls(extractUrls(markdown));
        }
      }
    },
    [text, setTextUrls, setText]
  );

  const onEdit = useCallback(
    (message?: Maybe<string>) => () => {
      if (message) {
        setText(message);
        resizeTextarea(message);
      }
    },
    [resizeTextarea, setText]
  );

  const handleSocketSuccess = useCallback(
    (payload: TSocketPayload) => {
      setIsPending(false);
      setAnimateMessages(true);
      refetchContexts();
      refetchChatContexts();

      const message = JSON.parse(payload.message);
      const chatId = payload.thread_id;
      if (!chatId) {
        setChatId(chatId.toString());
      }
      setTokens(payload.tokens ?? 0);
      setMessagesHistory((oldState) => [
        ...oldState,
        { role: message.role, content: message.content }
      ]);
      scrollToBottom();
    },
    [scrollToBottom]
  );

  const handleSocketError = (title: string, content: string) => () => {
    toaster.error(content, title);
  };

  useEffect(() => {
    scrollToBottom();
  }, [isPending, messagesHistory.length]);

  useEffect(() => {
    initial = false;
  }, []);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current?.focus();
    }
  }, [isPending]);

  useEffect(() => {
    if (chatId) {
      const chat = messages?.nodes?.at(0);
      const dataContexts = chatContexts?.nodes?.at(0);

      const engine = chat?.engine;
      const messagesChat = chat?.messages?.nodes;
      const contextsIdsChat = dataContexts?.contexts?.map(({ uploadedFile }) => uploadedFile.id);
      setChatInitialized(true);
      if (engine) setModel(engine);
      if (messagesChat && !messagesHistory.length)
        setMessagesHistory(messagesChat as TUniversalChatMessage[]);
      if (contextsIdsChat?.length) setContextIds(contextsIdsChat);
    }
  }, [chatId, messagesHistory, messages, chatContexts]);

  useEffect(() => {
    if (model === UniversalChatEngineEnum.Anthropic) setContextIds([]);
  }, [model, setContextIds]);

  useEffect(() => {
    setTokens(messages?.nodes?.at(0)?.tokens ?? 0);
  }, [messages]);

  useEffect(() => {
    if (
      socketConfig?.realtime.socketUrl &&
      socketConfig?.realtime.token &&
      socketConfig?.clientAccount.id &&
      socketConfig?.currentUser.id &&
      chatId &&
      !socketInitialized
    ) {
      const socket = new Socket(`${socketConfig?.realtime.socketUrl}/socket`, {
        reconnectAfterMs: (tries) => {
          // Custom reconnection logic (optional)
          return [1000, 2000, 5000][tries - 1] || 10000;
        },
        params: { token: socketConfig?.realtime.token }
      });
      socket.connect();

      const channel = socket.channel(
        `universal_chat:${socketConfig?.clientAccount.id}:${socketConfig?.currentUser.id}:thread:${chatId}`,
        {}
      );

      channel.join();

      channel.on('new:message', handleSocketSuccess);
      channel.on(
        'web:scraping:error',
        handleSocketError(
          t('messages.error.chat_scraping_error.title'),
          t('messages.error.chat_scraping_error.paragraph')
        )
      );
      channel.on(
        'file:processing:error',
        handleSocketError(
          t('messages.error.chat_file_error.title'),
          t('messages.error.chat_file_error.paragraph')
        )
      );
      channel.on(
        'error:message',
        handleSocketError(
          t('messages.error.chat_error.title'),
          t('messages.error.chat_error.paragraph')
        )
      );

      setSocketInitialized(true);
    }
  }, [
    socketConfig?.realtime.socketUrl,
    socketConfig?.realtime.token,
    socketConfig?.clientAccount.id,
    socketConfig?.currentUser.id,
    socketInitialized,
    handleSocketSuccess,
    handleSocketError,
    chatId,
    t
  ]);

  useEffect(() => {
    if (!text.length) resizeTextarea('');
    else resizeTextarea(text);
  }, [text, file?.name, resizeTextarea]);

  return (
    <>
      <div className="relative z-0 h-[calc(100vh_-_80px)] flex-1 flex-col overflow-hidden">
        <div className="flex h-full flex-1 bg-tertiary-900">
          <div className="flex flex-1 flex-col overflow-y-hidden">
            <Entering position="top" delay={0.2} duration={0.2} offset={10} initial={initial}>
              <div className="sticky top-0 shadow-[0_8px_8px_-8px_rgba(36,92,144,0.17)]">
                <div className="relative bg-white p-4 !pb-2">
                  <div className="mb-2 flex justify-end gap-2 px-4">
                    <Button
                      size="medium"
                      variant="contained"
                      label={t('chat.new_chat')}
                      onClick={onNewChat}
                    />
                  </div>
                </div>
              </div>
            </Entering>
            <div className="flex flex-1 flex-col justify-between overflow-y-hidden">
              {!(messagesHistory.length || chatId) ? (
                <Entering
                  position="top"
                  delay={0}
                  offset={10}
                  duration={0.3}
                  className="grid h-full place-content-center"
                >
                  <div>
                    <div className="flex flex-col items-center justify-center">
                      <AiIcon />
                      <Typography variant="medium4xl" className="text-center text-tertiary-100 ">
                        {t('chat.welcome.heading')}
                      </Typography>
                      <Typography variant="regular3" className="text-center text-tertiary-200 ">
                        {t('chat.welcome.description')}
                      </Typography>
                    </div>
                  </div>
                </Entering>
              ) : (
                <div
                  className="flex flex-col gap-8 overflow-scroll p-8 pb-16"
                  ref={chatContainerRef}
                >
                  <Entering position="top" delay={initial ? 0.6 : 0} offset={10} duration={0.3}>
                    {messagesHistory?.map(({ role, content, file }, index) => (
                      <ChatMsg
                        key={`${role} ${content} ${index}`}
                        title={messages?.nodes?.at(0)?.title}
                        index={index}
                        chatId={chatId}
                        message={content as string}
                        file={file}
                        assistant={role === UniversalChatRoleEnum.Assistant}
                        messageAnimated={index + 1 === messagesHistory.length && animateMessages}
                        onEdit={onEdit(content)}
                        stopAnimation={stopAnimation}
                        scrollToBottom={scrollToBottom}
                      />
                    ))}
                    {isPending && <ChatMsgProcessing />}
                  </Entering>
                </div>
              )}
              <Entering position="bottom" delay={0.2} duration={0.2} offset={5} initial={initial}>
                <div className="z-[80] mb-4">
                  <div className="tolq-chat-textarea">
                    <Attach
                      isPending={isPending}
                      onChange={handleLoadFile}
                      filename={file?.name}
                      onChangeAttachUrl={handleChangeAttachUrl}
                    />
                    <span className="w-full">
                      <div className="flex flex-wrap gap-0">
                        {file?.name && (
                          <Attached
                            onRemove={handleRemoteFile}
                            filename={file?.name}
                            className="ml-2 mt-2"
                          >
                            {file?.name}
                          </Attached>
                        )}
                        {urls?.map((url, index) => (
                          <Attached
                            key={url + index}
                            onRemove={handleRemoveLink}
                            className="ml-2 mt-2"
                            link={url}
                          />
                        ))}
                      </div>
                      <Textarea
                        placeholder={t('chat.placeholder')}
                        value={text || ''}
                        className={cn(
                          'mr-2 pb-[8px] pl-2 pr-3 !leading-6 ',
                          'max-h-52 w-full text-xl outline-none',
                          'mr-4 font-content font-light',
                          'resize-none rounded outline-none',
                          'placeholder:text-tertiary-400 disabled:bg-transparent disabled:text-tertiary-300',
                          file?.name ? 'pt-[10px]' : 'pt-[16px]'
                        )}
                        autoFocus
                        rows={1}
                        onChange={onChangeTextarea}
                        onKeyDown={onKeyDown}
                        onPaste={onPasteTextarea}
                        style={{ height }}
                        ref={inputRef}
                        disabled={isPending}
                      />
                    </span>
                    <div className="flex flex-1 flex-col justify-end">
                      {isPending ? (
                        <Button
                          size="small"
                          variant="icon"
                          color="none"
                          className="!m-0 !mb-3 !mr-3"
                          onClick={onStop}
                          startAdornment={<StopIcon className="!text-secondary-red" />}
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          className="!m-3 ml-0"
                          label={t('chat.send')}
                          onClick={onSubmit}
                          disabled={sendDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="m-2 mx-8 flex justify-end">
                    <Typography variant="light5" className="text-center text-tertiary-200 ">
                      {t('chat.token_usage')}: <strong>{tc(tokens, { digits: 2 })}</strong>
                    </Typography>
                  </div>
                </div>
              </Entering>
            </div>
          </div>
          <Entering
            position="right"
            delay={0.2}
            duration={0.2}
            offset={20}
            className="flex w-[20rem] shrink-0 flex-col"
            initial={initial}
          >
            <div className="z-[200] flex h-full flex-1 flex-col bg-white shadow-[0px_2px_8px_0px_rgba(36,92,144,0.17)]">
              <div className="sticky mt-1 border-b-1 border-tertiary-500 ">
                <div className="relative bg-white py-4 !pb-2">
                  <div className="mb-2 flex justify-between gap-2 px-6">
                    <Typography variant="regular2" className="text-tertiary-100 ">
                      {t('chat.title_menu')}
                    </Typography>
                    {false && (
                      <a
                        data-tooltip-id="tolq-tooltip-bottom-start"
                        data-tooltip-content={t('buttons.manage_task_prompt')}
                      >
                        <Button
                          size="small"
                          variant="icon"
                          color="none"
                          className="!m-0"
                          onClick={handleOpenManageTaskPrompt}
                          startAdornment={<TaskConfigIcon className="!text-tertiary-600" />}
                        />
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex h-full flex-1 flex-col overflow-y-auto p-6 !pt-0">
                <div>
                  <div className="flex">
                    <Label>{t('labels.model')}</Label>
                    <a
                      data-tooltip-id="tolq-info-top-start"
                      data-tooltip-html={t('chat.info.model')}
                    >
                      <InfoHollowIcon className="scale-50 cursor-pointer" />
                    </a>
                  </div>
                  <Select
                    selectedValue={model}
                    options={optionsModels}
                    placeholder={t('placeholders.model')}
                    onChange={onSelectModel}
                    noClose
                    disabled={chatInitialized}
                  />
                  {model === UniversalChatEngineEnum.Tolq_3b && (
                    <Hint>{t.raw('hints.llhama_take_long')}</Hint>
                  )}
                </div>
                {!!contexts?.nodes?.length && (
                  <div>
                    <div className="flex">
                      <Label>{t('labels.data_contexts')}</Label>
                      <a
                        data-tooltip-id="tolq-info-top-start"
                        data-tooltip-html={t('chat.info.data_contexts')}
                      >
                        <InfoHollowIcon className="hidden scale-50 cursor-pointer" />
                      </a>
                      <a
                        data-tooltip-id="tolq-info-top-start"
                        data-tooltip-html={t('chat.info.data_contexts')}
                      >
                        <InfoHollowIcon className="scale-50 cursor-pointer" />
                      </a>
                    </div>
                    <SelectTags
                      options={optionsContexts}
                      onChange={updateContextIds}
                      placeholder={
                        contextsDisabled
                          ? t('placeholders.data_contexts_no_more')
                          : t('placeholders.data_contexts')
                      }
                      selectedOptions={contextIds}
                      behaviorSelectedOptions="highlight"
                      showSelected={false}
                      placeholderSearch={t('placeholders.search_files')}
                      categoryOptions={optionsFilterTags}
                      onSelectCategory={setSelectedTagId as (value: string) => void}
                      category={selectedTagId}
                      className="mb-2"
                      classNameTags="!bg-primary-blue-900 !rounded-full"
                      classNameTagsTypography="!text-primary-blue-100"
                      noClose
                      disabled={contextsDisabled}
                      wideHeight
                      cleanOnSelect
                    />
                    {model === UniversalChatEngineEnum.Anthropic && (
                      <Hint>{t('hints.claude_sonnet_not_supported')}</Hint>
                    )}
                    {model === UniversalChatEngineEnum.Tolq_3b && (
                      <Hint>{t('hints.private_3b_not_supported')}</Hint>
                    )}
                    {!!contextsIncluded?.length && (
                      <div
                        className={cn(
                          'mb-2 flex flex-col gap-2',
                          contextsDisabled ? 'pointer-events-none !cursor-not-allowed' : ''
                        )}
                      >
                        {contextsIncluded?.map(({ filename, id }) => {
                          const fileNameisBig = (filename?.length ?? 0) > 50;

                          return (
                            <Tag
                              key={id}
                              onRemove={contextsDisabled ? undefined : deleteContextIds(id)}
                              className={cn(
                                'bg-tertiary-600',
                                fileNameisBig ? '!items-start' : '',
                                contextsDisabled ? 'hover:!scale-[1]' : 'hover:!scale-[1.01]'
                              )}
                              classNameTypography="!text-tertiary-200"
                              classNameCloseIcon="!text-tertiary-200"
                              classNameRemove={cn(fileNameisBig ? '!mt-1' : '')}
                            >
                              {filename}
                            </Tag>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                {!!chatList?.nodes.length && (
                  <div>
                    <Label>{t('labels.history')}</Label>
                    <div className="flex flex-col gap-4">
                      {chatList?.nodes?.map(({ title, id, updatedAt }) => (
                        <HistoryItem
                          key={id}
                          {...{
                            title,
                            id,
                            updatedAt,
                            chatId,
                            refetch: refetchChatList
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Entering>
        </div>
      </div>
      <ModalUploadContextLibrary
        open={openModalAddFile}
        defaultTags={defaultTags}
        onCancel={handleToggleModal}
        refetch={handleRefetch}
      />
      <ModalManageTaskPrompt open={openManageTaskPrompt} onCancel={handleCloseManageTaskPrompt} />
    </>
  );
}
